<script setup lang="ts">
import { ref, VNode, render, h, Component, isVNode } from "vue";
import {
  PromiseSchedule,
  generateUUID,
  CSSAnimationName
} from "@zyzgroup/core-common";
import {
  Easing,
  EasingFunction,
  // CSSToJSEasing,
  // reverseCSSToJSEasing,
  SupportedEaseNameToCubicBezier
} from "@zyzgroup/core-web";
// import { elementsToVNodes } from "../vnode";
import Page from "./Page.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Page },
  inheritAttrs: true
});

export type SupportedRouteEffect =
  | "moveToLeft"
  | "moveToRight"
  | "moveToTop"
  | "moveToBottom"
  | "moveFromLeftPrevFade"
  | "moveFromRightPrevFade"
  | "moveFromTopPrevFade"
  | "moveFromBottomPrevFade"
  | "moveToLeftFade"
  | "moveToRightFade"
  | "moveToTopFade"
  | "moveToBottomFade"
  | "moveToLeftEasing"
  | "moveToRightEasing"
  | "moveToTopEasing"
  | "moveToBottomEasing"
  | "moveFromLeftPrevScaleToSmall"
  | "moveFromRightPrevScaleToSmall"
  | "moveFromTopPrevScaleToSmall"
  | "moveFromBottomPrevScaleToSmall" // ⭐
  | "scaleSmall"
  | "scaleLarge"
  | "scaleToLargePrevScaleToSmall"
  | "scaleFromSmallPrevMoveToLeft"
  | "scaleFromSmallPrevMoveToRight"
  | "scaleFromSmallPrevMoveToTop"
  | "scaleFromSmallPrevMoveToBottom"
  | "glueToLeft"
  | "glueToRight"
  | "glueToTop"
  | "glueToBottom"
  | "flipToLeft"
  | "flipToRight"
  | "flipToTop"
  | "flipToBottom"
  | "rotateFall"
  | "newspaper"
  | "moveFromLeftPrevPush"
  | "moveFromRightPrevPush"
  | "moveFromTopPrevPush"
  | "moveFromBottomPrevPush"
  | "pullFromLeftPrevPush"
  | "pullFromRightPrevPush"
  | "pullFromTopPrevPush"
  | "pullFromBottomPrevPush"
  | "moveFromLeftFadePrevFold"
  | "moveFromRightFadePrevFold"
  | "moveFromTopFadePrevFold"
  | "moveFromBottomFadePrevFold"
  | "moveToLeftFadePrevUnfold"
  | "moveToRightFadePrevUnfold"
  | "moveToTopFadePrevUnfold"
  | "moveToBottomFadePrevUnfold"
  | "rotateRoomLeftIn"
  | "rotateRoomRightIn"
  | "rotateRoomTopIn"
  | "rotateRoomBottomIn"
  | "rotateCubeLeftIn"
  | "rotateCubeRightIn"
  | "rotateCubeTopIn"
  | "rotateCubeBottomIn"
  | "rotateCarouselLeftIn"
  | "rotateCarouselRightIn"
  | "rotateCarouselTopIn"
  | "rotateCarouselBottomIn"
  | "rotateSidesIn"
  | "rotateSlideIn";

const props = withDefaults(
  defineProps<{
    effect?: SupportedRouteEffect;
    duration?: number;
    delay?: number;
    timingFunction?: SupportedEaseNameToCubicBezier;
  }>(),
  {
    effect: "moveToLeft",
    duration: 500,
    delay: 0,
    timingFunction: "ease"
  }
);

const emits = defineEmits<{
  (
    event: "routeChangeStart",
    fromPageIndex: number,
    toPageIndex: number,
    totalPages: number
  ): void;
  (
    event: "routeChangeEnd",
    fromPageIndex: number,
    toPageIndex: number,
    totalPages: number
  ): void;
  (event: "renderComplete"): void;
}>();

type VNodeChildAtom =
  | VNode
  | string
  | number
  | boolean
  | null
  | undefined
  | void;
type VNodeArrayChildren = Array<VNodeArrayChildren | VNodeChildAtom>;
type RawChildren =
  | string
  | number
  | boolean
  | VNode
  | VNodeArrayChildren
  | (() => any);
type RawSlots = {
  [name: string]: unknown;
  $stable?: boolean;
};

export type RoutePage = {
  pageKey?: string;
  component: VNode | Component | HTMLElement;
  attributes?: Record<string, any>;
  child?: RawChildren | RawSlots;
  // routesPageAttributes?: InstanceType<typeof Page>;
};

const r = ref();
let lastResolve: (value: void | PromiseLike<void>) => void;
let shouldDeleteLastPage = false;

let currentPageIndex = -1;
let nextPageIndex = -1;

let pages: RoutePage[] = [];
let pageActions: {
  open: () => void;
  close: () => void;
}[] = [];
let pageElements: HTMLElement[] = [];

function isValidPageIndex(pageIndex: number): boolean {
  return pageIndex >= 0 && pageIndex < pages.length;
}

function isForward(): boolean {
  return nextPageIndex > currentPageIndex;
}

// page effect
function _getPageEffect(
  pageIndex: number,
  currentPageEffect: CSSAnimationName,
  nextPageEffect: CSSAnimationName
): CSSAnimationName {
  switch (pageIndex) {
    case currentPageIndex:
      return isForward() ? currentPageEffect : nextPageEffect;
    case nextPageIndex:
      return isForward() ? nextPageEffect : currentPageEffect;
    // default:
    //   return "static";
  }
}
function getPageEffect(
  routeEffect: SupportedRouteEffect,
  pageIndex: number
): CSSAnimationName {
  switch (routeEffect) {
    case "moveToLeft":
      return _getPageEffect(pageIndex, "moveToLeft", "moveFromRight");
    case "moveToRight":
      return _getPageEffect(pageIndex, "moveToRight", "moveFromLeft");
    case "moveToTop":
      return _getPageEffect(pageIndex, "moveToTop", "moveFromBottom");
    case "moveToBottom":
      return _getPageEffect(pageIndex, "moveToBottom", "moveFromTop");

    case "moveFromLeftPrevFade":
      return _getPageEffect(pageIndex, "fade", "moveFromLeft");
    case "moveFromRightPrevFade":
      return _getPageEffect(pageIndex, "fade", "moveFromRight");
    case "moveFromTopPrevFade":
      return _getPageEffect(pageIndex, "fade", "moveFromTop");
    case "moveFromBottomPrevFade":
      return _getPageEffect(pageIndex, "fade", "moveFromBottom");

    case "moveToLeftFade":
      return _getPageEffect(pageIndex, "moveToLeftFade", "moveFromRightFade");
    case "moveToRightFade":
      return _getPageEffect(pageIndex, "moveToRightFade", "moveFromLeftFade");
    case "moveToTopFade":
      return _getPageEffect(pageIndex, "moveToTopFade", "moveFromBottomFade");
    case "moveToBottomFade":
      return _getPageEffect(pageIndex, "moveToBottomFade", "moveFromTopFade");

    case "moveToLeftEasing":
      return _getPageEffect(pageIndex, "moveToLeft", "moveFromRight");
    case "moveToRightEasing":
      return _getPageEffect(pageIndex, "moveToRight", "moveFromLeft");
    case "moveToTopEasing":
      return _getPageEffect(pageIndex, "moveToTop", "moveFromBottom");
    case "moveToBottomEasing":
      return _getPageEffect(pageIndex, "moveToBottom", "moveFromTop");

    case "moveFromLeftPrevScaleToSmall":
      return _getPageEffect(pageIndex, "scaleToSmall", "moveFromLeft");
    case "moveFromRightPrevScaleToSmall":
      return _getPageEffect(pageIndex, "scaleToSmall", "moveFromRight");
    case "moveFromTopPrevScaleToSmall":
      return _getPageEffect(pageIndex, "scaleToSmall", "moveFromTop");
    case "moveFromBottomPrevScaleToSmall":
      return _getPageEffect(pageIndex, "scaleToSmall", "moveFromBottom");

    case "scaleSmall":
      return _getPageEffect(pageIndex, "scaleToSmall", "scaleFromLarge");
    case "scaleLarge":
      return _getPageEffect(pageIndex, "scaleToLarge", "scaleFromSmall");
    case "scaleToLargePrevScaleToSmall":
      return _getPageEffect(
        pageIndex,
        "scaleToSmallCenter",
        "scaleFromSmallCenter"
      );

    case "scaleFromSmallPrevMoveToLeft":
      return _getPageEffect(pageIndex, "moveToLeft", "scaleFromSmall");
    case "scaleFromSmallPrevMoveToRight":
      return _getPageEffect(pageIndex, "moveToRight", "scaleFromSmall");
    case "scaleFromSmallPrevMoveToTop":
      return _getPageEffect(pageIndex, "moveToTop", "scaleFromSmall");
    case "scaleFromSmallPrevMoveToBottom":
      return _getPageEffect(pageIndex, "moveToBottom", "scaleFromSmall");

    case "glueToLeft":
      return _getPageEffect(pageIndex, "rotateRightSideFirst", "moveFromRight");
    case "glueToRight":
      return _getPageEffect(pageIndex, "rotateLeftSideFirst", "moveFromLeft");
    case "glueToTop":
      return _getPageEffect(
        pageIndex,
        "rotateBottomSideFirst",
        "moveFromBottom"
      );
    case "glueToBottom":
      return _getPageEffect(pageIndex, "rotateTopSideFirst", "moveFromTop");

    case "flipToLeft":
      return _getPageEffect(pageIndex, "flipOutLeft", "flipInRight");
    case "flipToRight":
      return _getPageEffect(pageIndex, "flipOutRight", "flipInLeft");
    case "flipToTop":
      return _getPageEffect(pageIndex, "flipOutTop", "flipInBottom");
    case "flipToBottom":
      return _getPageEffect(pageIndex, "flipOutBottom", "flipInTop");

    case "rotateFall":
      return _getPageEffect(pageIndex, "rotateFall", "scaleFromSmall");

    case "newspaper":
      return _getPageEffect(
        pageIndex,
        "rotateOutNewspaper",
        "rotateInNewspaper"
      );

    case "moveFromLeftPrevPush":
      return _getPageEffect(pageIndex, "rotatePushRight", "moveFromLeft");
    case "moveFromRightPrevPush":
      return _getPageEffect(pageIndex, "rotatePushLeft", "moveFromRight");
    case "moveFromTopPrevPush":
      return _getPageEffect(pageIndex, "rotatePushBottom", "moveFromTop");
    case "moveFromBottomPrevPush":
      return _getPageEffect(pageIndex, "rotatePushTop", "moveFromBottom");

    case "pullFromLeftPrevPush":
      return _getPageEffect(pageIndex, "rotatePushRight", "rotatePullLeft");
    case "pullFromRightPrevPush":
      return _getPageEffect(pageIndex, "rotatePushLeft", "rotatePullRight");
    case "pullFromTopPrevPush":
      return _getPageEffect(pageIndex, "rotatePushBottom", "rotatePullTop");
    case "pullFromBottomPrevPush":
      return _getPageEffect(pageIndex, "rotatePushTop", "rotatePullBottom");

    case "moveFromLeftFadePrevFold":
      return _getPageEffect(pageIndex, "rotateFoldRight", "moveFromLeftFade");
    case "moveFromRightFadePrevFold":
      return _getPageEffect(pageIndex, "rotateFoldLeft", "moveFromRightFade");
    case "moveFromTopFadePrevFold":
      return _getPageEffect(pageIndex, "rotateFoldBottom", "moveFromTopFade");
    case "moveFromBottomFadePrevFold":
      return _getPageEffect(pageIndex, "rotateFoldTop", "moveFromBottomFade");

    case "moveToLeftFadePrevUnfold":
      return _getPageEffect(pageIndex, "moveToLeftFade", "rotateUnfoldRight");
    case "moveToRightFadePrevUnfold":
      return _getPageEffect(pageIndex, "moveToRightFade", "rotateUnfoldLeft");
    case "moveToTopFadePrevUnfold":
      return _getPageEffect(pageIndex, "moveToTopFade", "rotateUnfoldBottom");
    case "moveToBottomFadePrevUnfold":
      return _getPageEffect(pageIndex, "moveToBottomFade", "rotateUnfoldTop");

    case "rotateRoomLeftIn":
      return _getPageEffect(pageIndex, "rotateRoomLeftOut", "rotateRoomLeftIn");
    case "rotateRoomRightIn":
      return _getPageEffect(
        pageIndex,
        "rotateRoomRightOut",
        "rotateRoomRightIn"
      );
    case "rotateRoomTopIn":
      return _getPageEffect(pageIndex, "rotateRoomTopOut", "rotateRoomTopIn");
    case "rotateRoomBottomIn":
      return _getPageEffect(
        pageIndex,
        "rotateRoomBottomOut",
        "rotateRoomBottomIn"
      );

    case "rotateCubeLeftIn":
      return _getPageEffect(pageIndex, "rotateCubeLeftOut", "rotateCubeLeftIn");
    case "rotateCubeRightIn":
      return _getPageEffect(
        pageIndex,
        "rotateCubeRightOut",
        "rotateCubeRightIn"
      );
    case "rotateCubeTopIn":
      return _getPageEffect(pageIndex, "rotateCubeTopOut", "rotateCubeTopIn");
    case "rotateCubeBottomIn":
      return _getPageEffect(
        pageIndex,
        "rotateCubeBottomOut",
        "rotateCubeBottomIn"
      );

    case "rotateCarouselLeftIn":
      return _getPageEffect(
        pageIndex,
        "rotateCarouselLeftOut",
        "rotateCarouselLeftIn"
      );
    case "rotateCarouselRightIn":
      return _getPageEffect(
        pageIndex,
        "rotateCarouselRightOut",
        "rotateCarouselRightIn"
      );
    case "rotateCarouselTopIn":
      return _getPageEffect(
        pageIndex,
        "rotateCarouselTopOut",
        "rotateCarouselTopIn"
      );
    case "rotateCarouselBottomIn":
      return _getPageEffect(
        pageIndex,
        "rotateCarouselBottomOut",
        "rotateCarouselBottomIn"
      );

    case "rotateSidesIn":
      return _getPageEffect(pageIndex, "rotateSidesOut", "rotateSidesIn");
    case "rotateSlideIn":
      return _getPageEffect(pageIndex, "rotateSlideOut", "rotateSlideIn");
  }
}

// duration
function _getPageDuration(
  pageIndex: number,
  currentDuration = 600,
  nextDuration = 600
): number {
  switch (pageIndex) {
    case currentPageIndex:
      return currentDuration;
    case nextPageIndex:
      return nextDuration;
    default:
      return props.duration;
  }
}
function getPageDuration(
  routeEffect: SupportedRouteEffect,
  pageIndex: number
): number {
  switch (routeEffect) {
    case "moveFromLeftFadePrevFold":
    case "moveFromRightFadePrevFold":
    case "moveFromTopFadePrevFold":
    case "moveFromBottomFadePrevFold":
      return _getPageDuration(pageIndex, 700, 700);
    case "moveToLeftEasing":
    case "moveToRightEasing":
    case "moveToTopEasing":
    case "moveToBottomEasing":
    case "moveFromLeftPrevScaleToSmall":
    case "moveFromRightPrevScaleToSmall":
    case "moveFromTopPrevScaleToSmall":
    case "moveFromBottomPrevScaleToSmall":
      return _getPageDuration(pageIndex, 700, 600);
    case "scaleSmall":
      return _getPageDuration(pageIndex, 700, 500);
    case "scaleLarge":
      return _getPageDuration(pageIndex, 500, 700);
    case "scaleToLargePrevScaleToSmall":
      return _getPageDuration(pageIndex, 400, 400);
    case "scaleFromSmallPrevMoveToLeft":
    case "scaleFromSmallPrevMoveToRight":
    case "scaleFromSmallPrevMoveToTop":
    case "scaleFromSmallPrevMoveToBottom":
      return _getPageDuration(pageIndex, 600, 700);
    case "glueToLeft":
    case "glueToRight":
    case "glueToTop":
    case "glueToBottom":
      return _getPageDuration(pageIndex, 800, 600);
    default:
      return props.duration;
  }
}

// delay
function _getPageDelay(pageIndex: number, delay = 300): number {
  switch (pageIndex) {
    case nextPageIndex:
      return props.delay + delay;
    default:
      return props.delay;
  }
}
function getPageDelay(
  routeEffect: SupportedRouteEffect,
  pageIndex: number
): number {
  switch (routeEffect) {
    case "flipToLeft":
    case "flipToRight":
    case "flipToTop":
    case "flipToBottom":
    case "newspaper":
      return _getPageDelay(pageIndex, 500);
    case "scaleSmall":
    case "scaleLarge":
      return _getPageDelay(pageIndex, 300);
    case "scaleToLargePrevScaleToSmall":
      return _getPageDelay(pageIndex, 400);
    case "glueToLeft":
    case "glueToRight":
    case "glueToTop":
    case "glueToBottom":
    case "rotateSidesIn":
      return _getPageDelay(pageIndex, 200);
    case "pullFromLeftPrevPush":
    case "pullFromRightPrevPush":
    case "pullFromTopPrevPush":
    case "pullFromBottomPrevPush":
      return _getPageDelay(pageIndex, 180);
    default:
      return props.delay;
  }
}

let currentZIndex = 2;
let nextZIndex = 2;
const sameOnTop = () => {
  currentZIndex = 2;
  nextZIndex = 2;
};
const nextOnTop = () => {
  currentZIndex = 2;
  nextZIndex = 3;
};
const currentOnTop = () => {
  currentZIndex = 3;
  nextZIndex = 2;
};
function setRunningStyle(pageIndex: number) {
  switch (props.effect) {
    case "moveToLeftEasing":
    case "moveToRightEasing":
    case "moveToTopEasing":
    case "moveToBottomEasing":
      currentOnTop();
      break;
    case "scaleFromSmallPrevMoveToLeft":
    case "scaleFromSmallPrevMoveToRight":
    case "scaleFromSmallPrevMoveToTop":
    case "scaleFromSmallPrevMoveToBottom":
    case "rotateFall":
    case "rotateRoomLeftIn":
    case "rotateRoomRightIn":
    case "rotateRoomTopIn":
    case "rotateRoomBottomIn":
    case "rotateCubeLeftIn":
    case "rotateCubeRightIn":
    case "rotateCubeTopIn":
    case "rotateCubeBottomIn":
    case "rotateCarouselLeftIn":
    case "rotateCarouselRightIn":
    case "rotateCarouselTopIn":
    case "rotateCarouselBottomIn":
      isForward() ? currentOnTop() : nextOnTop();
      break;
    case "moveFromLeftPrevFade":
    case "moveFromRightPrevFade":
    case "moveFromTopPrevFade":
    case "moveFromBottomPrevFade":
    case "moveFromLeftPrevScaleToSmall":
    case "moveFromRightPrevScaleToSmall":
    case "moveFromTopPrevScaleToSmall":
    case "moveFromBottomPrevScaleToSmall":
    case "glueToLeft":
    case "glueToRight":
    case "glueToTop":
    case "glueToBottom":
      isForward() ? nextOnTop() : currentOnTop();
      break;
    default:
      sameOnTop();
      break;
  }
  if (isValidPageIndex(currentPageIndex) && currentPageIndex == pageIndex) {
    pageElements[currentPageIndex].style.zIndex = `${currentZIndex}`;
    pageElements[currentPageIndex].style.visibility = "visible";
  }
  if (isValidPageIndex(nextPageIndex) && nextPageIndex == pageIndex) {
    pageElements[nextPageIndex].style.zIndex = `${nextZIndex}`;
    pageElements[nextPageIndex].style.visibility = "visible";
  }
}

function runningEnd(pageIndex: number) {
  nextOnTop();
  if (pageIndex == currentPageIndex) {
    pageElements[pageIndex].style.zIndex = `${currentZIndex}`;
    pageElements[pageIndex].style.visibility = "hidden";
  } else if (pageIndex == nextPageIndex) {
    pageElements[pageIndex].style.zIndex = `${nextZIndex}`;
    pageElements[pageIndex].style.visibility = "visible";
    if (shouldDeleteLastPage) {
      shouldDeleteLastPage = false;
      pages.splice(pages.length - 1, 1);
      renderPages();
    }
    emits("routeChangeEnd", currentPageIndex, nextPageIndex, pages.length);
    currentPageIndex = nextPageIndex;
    lastResolve?.();
    lastResolve = undefined;
  }
}

function renderPages() {
  if (!r.value) {
    return;
  }
  const vnodes: VNode[] = pages.map((page, i) => {
    if (isVNode(page.component)) {
      // 无 key 不会重复利用
      page.component.key = page.pageKey;
    }
    return h(
      Page,
      {
        key: "routes-page-" + i,
        class: "routes-page-" + i,
        style: {
          position: "absolute",
          visibility: pageElements[i]?.style.visibility || "hidden",
          zIndex: pageElements[i]?.style.zIndex || "1"
        },
        // ...page.routesPageAttributes,
        name: getPageEffect(props.effect, i),
        duration: getPageDuration(props.effect, i),
        delay: getPageDelay(props.effect, i),
        timingFunction: props.timingFunction,
        onPageOpenStart: () => {
          setRunningStyle(i);
        },
        onPageOpenEnd: () => {
          runningEnd(i);
        },
        onPageCloseStart: () => {
          setRunningStyle(i);
        },
        onPageCloseEnd: () => {
          runningEnd(i);
        }
      },
      // () => h(page.component, page.attributes)
      {
        default: () =>
          isVNode(page.component)
            ? page.component
            : page.component instanceof HTMLElement
            ? h(
                "div",
                {
                  ...page.attributes,
                  key: page.pageKey, // 无 key 不会重复利用
                  innerHTML: page.component.innerHTML // ce 动态设置的 props 丢失 ！
                }
                // elementsToVNodes(page.component)
              )
            : h(
                page.component,
                {
                  ...page.attributes,
                  key: page.pageKey // 无 key 不会重复利用
                },
                () => page.child
              )
      }
    );
  });
  render(
    h(
      "div",
      {
        key: "routes-pages",
        class: "routes-pages",
        style: {
          display: "block",
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden"
        }
      },
      vnodes
    ),
    r.value
  );

  // 渲染结束
  emits("renderComplete");

  pageElements = r.value.children[0].children;

  vnodes.forEach((v, i) => {
    pageActions[i] = {
      open: v.component?.exposed?.open || (() => {}),
      close: v.component?.exposed?.close || (() => {})
    };
  });
}

function _goto(index: number, _shouldDeleteLastPage = false): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (!isValidPageIndex(index)) {
      return reject(`Page:${index} is invalid!`);
    }
    if (index == currentPageIndex) {
      return reject(`Page:${index} is currentPageIndex!`);
    }
    lastResolve = resolve;
    shouldDeleteLastPage = _shouldDeleteLastPage;
    nextPageIndex = index;
    emits("routeChangeStart", currentPageIndex, nextPageIndex, pages.length);
    renderPages();
    if (isForward()) {
      // forward: page.open
      if (isValidPageIndex(currentPageIndex)) {
        pageActions[currentPageIndex].open();
      }
      if (isValidPageIndex(nextPageIndex)) {
        pageActions[nextPageIndex].open();
      }
    } else {
      // back: page.close
      if (isValidPageIndex(currentPageIndex)) {
        pageActions[currentPageIndex].close();
      }
      if (isValidPageIndex(nextPageIndex)) {
        pageActions[nextPageIndex].close();
      }
    }
    // 执行动画...
  });
}

const schedule = new PromiseSchedule<void>({ concurrency: 1 });

function reset(resetPages: RoutePage[] = []): void {
  schedule.asap(async () => {
    pages = [...resetPages];
    pages.forEach((p) => {
      p.pageKey = p.pageKey || generateUUID();
    });
    currentPageIndex = -1;
    await _goto(0);
  });
}

function push(page: RoutePage): void {
  schedule.asap(async () => {
    page.pageKey = page.pageKey || generateUUID();
    pages.push(page);
    await _goto(pages.length - 1);
  });
}

// 动画完成后，删除最后一页
function pop(): void {
  schedule.asap(async () => {
    await _goto(pages.length - 2, true);
  });
}

function goto(index: number): void {
  schedule.asap(async () => {
    await _goto(index);
  });
}

function forward(): Promise<void> {
  if (lastResolve) {
    return Promise.reject("isRunning");
  }
  return _goto(currentPageIndex + 1);
}

function back(): Promise<void> {
  if (lastResolve) {
    return Promise.reject("isRunning");
  }
  return _goto(currentPageIndex - 1);
}

defineExpose({
  reset,
  push,
  pop,
  goto,
  forward,
  back,
  currentPageIndex: () => currentPageIndex,
  totalPages: () => pages.length
});
</script>

<template>
  <div
    ref="r"
    class="routes"
    style="
      display: block;
      width: 100%;
      height: 100%;
      position: relative;
      overflow: hidden;
    "
  ></div>
</template>
